import {useModalAction} from '@/_application/core/components/ui/modal/modal.context';
import {toast} from 'react-toastify';
import {authorizationAtom} from '@/store/authorization-atom';
import {useAtom} from 'jotai';
import {avatarPlaceholder} from '@/lib/placeholders';

import {
    useCurrentCustomerQuery,
    useInitiatePasswordResetByPhoneMutation,
    useResetPasswordByOtpMutation,
    useValidateResetPasswordTokenMutation,
} from './gql/auth.graphql';
import {
    Customer,
    LoginDevice,
    NotificationChannel,
    RegisterAccountInfo,
    ValidatePhoneType,
} from '__generated__/__types__';
import {useState} from 'react';
import {useStateMachine} from 'little-state-machine';
import {updateFormState, initialState} from '@/_application/modules/auth/components/update-password/forgot-password';
import {
    useConfirmRequestedValidationPhoneMutation,
    useRegisterNewAccountByOtpMutation,
    useRequestValidatePhoneMutation,
} from '__generated__/src/framework/graphql/gql/auth.graphql';
import {useTranslation} from 'next-i18next';

export function useUser() {
    const [isAuthorized] = useAtom(authorizationAtom);
    const {
        data,
        loading: isLoading,
        error,
    } = useCurrentCustomerQuery({
        fetchPolicy: 'network-only',
        skip: !isAuthorized,
        onError: err => {
            console.log(err);
        },
    });
    return {
        me: {
            info: {
                ...((data?.customer ?? {}) as Customer),
            },
            wallet: {
                available_points: 0,
                total_points: 10,
                points_used: 100,
            },
            profile: {
                avatar: {
                    // example avatar
                    thumbnail: avatarPlaceholder,
                },
            },
        },
        isLoading,
        error,
        isAuthorized,
    };
}

export function useForgotPassword() {
    const {actions} = useStateMachine({updateFormState});
    let [message, setMessage] = useState<string | null>(null);
    let [formError, setFormError] = useState<any>(null);

    const [initiatePasswordResetByPhoneMutation, {loading: isLoading, error}] =
        useInitiatePasswordResetByPhoneMutation();

    function forget(phoneNumber: string) {
        return initiatePasswordResetByPhoneMutation({
            variables: {
                phoneNumber,
            },
            onCompleted: res => {
                if (!res?.initiatePasswordResetByPhone?.secret) {
                    setFormError({email: error?.message!});
                    return;
                }
                setMessage(error?.message!);
                actions.updateFormState({
                    email: phoneNumber,
                    step: 'Token',
                    secret: res?.initiatePasswordResetByPhone?.secret!,
                });
            },
            onError: err => {
                toast.error(err.message);
            },
        });
    }

    return {mutate: forget, isLoading, message, formError, setMessage};
}

export function useResetPassword() {
    const {openModal} = useModalAction();
    const [resetPasswordByOtpMutation, {loading: isLoading, error}] = useResetPasswordByOtpMutation();
    const {actions} = useStateMachine({updateFormState});

    function reset(input: {phoneNumber: string; otp: string; secret: string; newPassword: string}) {
        return resetPasswordByOtpMutation({
            variables: {
                ...input,
            },
            onCompleted: res => {
                if (res?.resetPasswordByOtp?.success) {
                    toast.success(res?.resetPasswordByOtp?.message);
                    actions.updateFormState({
                        ...initialState,
                    });
                    openModal('LOGIN_VIEW');
                    return;
                }

                if (error && error.message) {
                    toast.error(error.message);
                }
                return;
            },
            onError: err => {
                toast.error(err.message);
            },
        });
    }

    return {mutate: reset, isLoading};
}

export function useVerifyForgotPasswordToken() {
    const {actions} = useStateMachine({updateFormState});
    const [validateResetPasswordTokenMutation, {loading: isLoading, error}] = useValidateResetPasswordTokenMutation();
    let [formError, setFormError] = useState<any>(null);

    function verify(input: {phoneNumber: string; otp: string; secret: string}) {
        return validateResetPasswordTokenMutation({
            variables: {
                ...input,
            },
            onCompleted: res => {
                if (!res.validateResetPasswordToken?.success) {
                    setFormError({token: error?.message!});
                    return;
                }
                actions.updateFormState({
                    step: 'Password',
                    token: input.otp,
                    secret: input.secret,
                });
            },
            onError: err => {
                toast.error(err.message);
            },
        });
    }

    return {mutate: verify, isLoading, formError};
}

export function useRequestValidateVerifyPhone() {
    const {t} = useTranslation('common');
    let [serverError, setServerError] = useState<string | null>(null);
    const [requestValidatePhoneMutation, {loading: isLoading, error}] = useRequestValidatePhoneMutation();
    function verifyPhone(input: {
        phoneNumber: string;
        validateType: ValidatePhoneType;
        notificationChannel: NotificationChannel;
        captchaResponseToken: string;
        sign: string;
    }) {
        return requestValidatePhoneMutation({
            variables: {
                ...input,
            },
            onCompleted: res => {
                if (!res.requestValidatePhone?.success) {
                    let errorMessage: string;
                    switch (res.requestValidatePhone.error_code) {
                        case '1001':
                            toast.error(t('error-already_request'));
                            errorMessage = t('error-already_request');
                            break;

                        case '1002':
                            toast.error(t('error-exits_phone'));
                            errorMessage = t('error-exits_phone');
                            break;

                        case '1004':
                            toast.error(t('error-phone-not-exist'));
                            errorMessage = t('error-phone-not-exist');
                            break;

                        default:
                            toast.error(res.requestValidatePhone?.message);
                            errorMessage = res.requestValidatePhone?.message ?? 'error-credential-wrong';
                            break;
                    }
                    setServerError(errorMessage);
                    return;
                }
                if (error?.message) {
                    toast.error(error.message);
                    setServerError(error.message ?? 'error-credential-wrong');
                    return;
                }

                return res;
            },
            onError: err => {
                setServerError(err.message);
                toast.error(err.message);
            },
        });
    }

    return {mutate: verifyPhone, isLoading, serverError, setServerError};
}

export function useVerifyOtp() {
    const [confirmRequestedValidationPhoneMutation, {loading: isLoading, error}] =
        useConfirmRequestedValidationPhoneMutation();
    function verifyOtp(input: {phoneNumber: string; otp: string; secret: string}) {
        return confirmRequestedValidationPhoneMutation({
            variables: {
                ...input,
            },
            onCompleted: res => {
                return res;
            },
            onError: err => {
                toast.error(err.message);
            },
        });
    }

    return {mutate: verifyOtp, isLoading, error};
}

export function useRegisterNewAccountByOtp() {
    const [registerNewAccountByOtpMutation, {loading: isLoading, error}] = useRegisterNewAccountByOtpMutation();
    function registerNewAccount(input: {
        phoneNumber: string;
        otp: string;
        secret: string;
        accountInfo: RegisterAccountInfo;
        deviceRequest: LoginDevice;
    }) {
        return registerNewAccountByOtpMutation({
            variables: {
                ...input,
            },
            onCompleted: res => {
                if (!res.registerNewAccountByOtp?.success) {
                    toast.error(res.registerNewAccountByOtp?.message);
                    return;
                }
                return res;
            },
            onError: err => {
                toast.error(err.message);
            },
        });
    }

    return {mutate: registerNewAccount, isLoading, error};
}
